import React from "react";
import Spinner from "../common/Spinner";

// ISO-2 country code -> display name (via Intl) and flag emoji. The realtime
// breakdown uses ISO-2 codes plus the bucket "REST" (everyone else).
const regionNames =
  typeof Intl !== "undefined" && (Intl as any).DisplayNames
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

export function countryLabel(code: string): string {
  if (!code) return "Unknown";
  if (code === "REST") return "Other";
  if (!/^[A-Za-z]{2}$/.test(code)) return code;
  try {
    return regionNames?.of(code.toUpperCase()) || code.toUpperCase();
  } catch {
    return code.toUpperCase();
  }
}

export function countryFlag(code: string): string {
  if (!/^[A-Za-z]{2}$/.test(code)) return "🌍";
  const cc = code.toUpperCase();
  return String.fromCodePoint(...[...cc].map((c) => 0x1f1e6 + c.charCodeAt(0) - 65));
}

export interface CountrySplit {
  items: { country: string; count: number }[];
  total: number;
  latestTs: number;
}

// "Subscribers by country" — the latest-snapshot split for the selected target.
const CountryBreakdown: React.FC<{
  loading: boolean;
  notEnabled: boolean;
  error: string | null;
  split: CountrySplit | null;
  subtitle?: string; // top-right note; defaults to "latest snapshot · <latestTs>"
  totalLabel?: string; // bottom-line label; defaults to "Total present"
}> = ({ loading, notEnabled, error, split, subtitle, totalLabel = "Total present" }) => {
  if (loading)
    return (
      <div className="py-16">
        <Spinner />
      </div>
    );
  if (notEnabled)
    return (
      <div className="rounded-2xl border border-warn-line bg-warn-soft p-6 text-sm text-warn-ink">
        The country breakdown isn't enabled for this account. An admin can turn on the{" "}
        <span className="font-mono">realtime_data_country</span> view to see it here.
      </div>
    );
  if (error)
    return (
      <div className="rounded-2xl border border-bad-line bg-bad-soft p-6 text-bad-ink">{error}</div>
    );
  if (!split || !split.items.length)
    return (
      <div className="rounded-2xl border border-paper-300/70 bg-surface p-10 text-center text-ink-muted">
        No country breakdown for this target yet.
      </div>
    );

  const max = split.items[0].count || 1;
  return (
    <div className="rounded-2xl border border-paper-300/70 bg-surface p-5 shadow-soft">
      <div className="mb-4 flex items-baseline justify-between">
        <h2 className="font-display text-lg font-semibold text-ink">Subscribers by country</h2>
        <span className="text-xs text-ink-faint">
          {subtitle ?? `latest snapshot · ${new Date(split.latestTs).toLocaleString()}`}
        </span>
      </div>
      <div className="space-y-2.5">
        {split.items.map((it) => {
          const pct = split.total ? (it.count / split.total) * 100 : 0;
          return (
            <div key={it.country} className="flex items-center gap-3">
              <span className="flex w-36 shrink-0 items-center gap-1.5 truncate text-sm text-ink" title={it.country}>
                <span aria-hidden>{countryFlag(it.country)}</span>
                <span className="truncate">{countryLabel(it.country)}</span>
              </span>
              <div className="relative h-6 flex-1 overflow-hidden rounded bg-paper-100">
                <div
                  className="absolute inset-y-0 left-0 rounded bg-brand-500/80"
                  style={{ width: `${(it.count / max) * 100}%` }}
                />
              </div>
              <span className="w-24 shrink-0 text-right tabular-nums text-sm text-ink">
                {it.count.toLocaleString()}
                <span className="ml-1 text-xs text-ink-faint">{pct.toFixed(0)}%</span>
              </span>
            </div>
          );
        })}
      </div>
      <p className="mt-4 text-xs text-ink-faint">
        {totalLabel}: {split.total.toLocaleString()} · {split.items.length}{" "}
        {split.items.length === 1 ? "country" : "countries"}
      </p>
    </div>
  );
};

export default CountryBreakdown;
